import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { AuthLink, AuthShell, Divider, GoogleButton } from "@/pages/auth/AuthShell";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const dest = location.state?.from || "/dashboard";

  useEffect(() => {
    if (user) navigate(dest, { replace: true });
  }, [user, dest, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(form.email, form.password);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell eyebrow="Welcome back" title="Sign in to the Foundry"
      footer={<>New here? <AuthLink to="/register" testid="login-register-link">Create an account</AuthLink></>}>
      <GoogleButton />
      <Divider />
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="field-label" htmlFor="login-email">Email</label>
          <input id="login-email" type="email" required autoComplete="email" className="field" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="field-label" htmlFor="login-password">Password</label>
            <AuthLink to="/forgot-password" testid="login-forgot-password-link"><span className="text-xs">Forgot?</span></AuthLink>
          </div>
          <input id="login-password" type="password" required autoComplete="current-password" className="field" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" />
        </div>
        {error && <p role="alert" className="rounded-md border border-red-800/60 bg-red-950/40 px-3 py-2 text-sm text-red-300" data-testid="login-error">{error}</p>}
        <button type="submit" className="btn-gold w-full" disabled={busy} data-testid="login-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}Sign in
        </button>
      </form>
    </AuthShell>
  );
}
